import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import UserAction from "../actions/UserAction";
import LayoutAction from "../actions/LayoutAction";
import {
  connectApp,
  getAppLink,
  getCurrentAzureOwner,
  getCurrentGoogleOwner,
  openLink,
} from "../api/AppLinkApi";
import { getBaseUrl } from "../api/api";
import { PackageMinus, PackagePlus } from "lucide-react";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import { connectRedirectUrlAppIntegration } from "../Navigator";
import { useParams } from "react-router-dom";
import useUpgradePlan from "../team/hooks/useUpgradePlan";
import RuleAction from "../actions/RuleAction";

const EmailSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();
  const { invalidIntegration } = useUpgradePlan();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  const [appGoogleData, setAppGoogleData] = useState<any>({});
  const [appAzureData, setAppAzureData] = useState<any>({});

  useEffect(() => {
    loadGoogleData();
    loadAzureData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGoogleData = () => {
    getAppLink("google").then((data) => {
      if (data.connected) {
        getCurrentGoogleOwner().then((dataUser) => {
          setAppGoogleData({
            app: "google",
            name: dataUser.name,
            email: dataUser.email,
            ...data,
          });
        });
      } else {
        setAppGoogleData({
          app: "google",
          name: t("Anonymous"),
          email: "",
          ...data,
        });
      }
    });
  };

  const loadAzureData = () => {
    getAppLink("azure").then((data) => {
      if (data.connected) {
        getCurrentAzureOwner().then((dataUser) => {
          setAppAzureData({
            app: "azure",
            name: dataUser.displayName,
            email: dataUser.mail,
            ...data,
          });
        });
      } else {
        setAppAzureData({
          app: "azure",
          name: t("Anonymous"),
          email: "",
          ...data,
        });
      }
    });
  };

  const handleConnectApp = (app) => {
    if (invalidIntegration(app.app)) return;
    connectAppApi(app);
  };

  const handleDisconnectApp = (app) => {
    setShowConfirm({
      show: true,
      title: t("Disconnect App"),
      description: t("Are you sure you want to disconnect this app?"),
      type: "DISCONNECT_APP",
      data: app,
    });
  };

  const connectAppApi = (app) => {
    const postUrl = connectRedirectUrlAppIntegration(projectId, app.app);
    connectApp(projectId, app.app, postUrl)
      .then((newAppData) => {
        const { redirectUrl = "" } = newAppData;
        if (redirectUrl != "" && !app.connected) {
          openLink(redirectUrl);
        } else {
          if (app.app === "google") {
            loadGoogleData();
          }
          if (app.app === "azure") {
            loadAzureData();
          }
          dispatch(
            LayoutAction.setNotification({
              title: t("App"),
              type: "success",
              message: app.connected
                ? t("App disconnected successfully!")
                : t("App connected successfully!"),
            })
          );
        }
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("App"),
            type: "error",
            message: app.connected ? t("App disconnected failed!") : t("App connected failed!"),
          })
        );
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(RuleAction.getAppLinks());
        }, 500);
      });
  };

  const handleSubmit = () => {
    if (showConfirm.type === "DISCONNECT_APP") {
      connectAppApi(showConfirm.data);
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleSetDefault = async (currentInboxAppId) => {
    await dispatch(
      UserAction.updateCurrentUserSetting({
        currentInboxAppId: currentInboxAppId,
        displayName: currentUser.get("displayName"),
        language: currentUser.get("language"),
        tz: currentUser.get("tz"),
      })
    );
    dispatch(
      LayoutAction.setNotification({
        title: t("Set Default"),
        type: "success",
        message: t("Set default successfully!"),
      })
    );
  };

  return (
    <div className="p-4 max-w-5xl">
      <div className="px-4 sm:px-0">
        <h3 className="text-base/7 font-semibold text-gray-900">{t("Email & Calendar")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          {t("Manage and sync your email and calendar accounts to stay organized")}
        </p>
      </div>
      <div className="mt-6">
        <div className="mb-4">
          <div className="font-semibold text-gray-900">{t("Connected accounts")}</div>
        </div>
        <div className="flex items-center justify-between p-2 mb-4 border border-gray-200 rounded-lg shadow-xs">
          <div className="flex items-center space-x-4">
            <div className="pl-2">
              <img
                alt="Google"
                src={`${getBaseUrl()}/static/images/google.png`}
                className="size-5"
              />
            </div>
            <div>
              <div className="text-base font-semibold leading-6 text-gray-900">
                {appGoogleData.connected ? appGoogleData.name : t("Connect your Google account")}
              </div>
              <div className="mt-1 text-sm leading-5 text-gray-500">
                {appGoogleData.connected && appGoogleData.email
                  ? appGoogleData.email
                  : t("Email and Calendar")}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end space-x-2">
            {appGoogleData.connected && currentUser.get("currentInboxAppId") !== "google" ? (
              <button
                onClick={() => handleSetDefault("google")}
                type="button"
                className="flex items-center rounded-md px-3 py-2 text-xs font-semibold cursor-pointer text-blue-600"
              >
                {t("Set Default")}
              </button>
            ) : null}
            <button
              onClick={() =>
                appGoogleData.connected
                  ? handleDisconnectApp(appGoogleData)
                  : handleConnectApp(appGoogleData)
              }
              type="button"
              className={`flex items-center rounded-md px-3 py-2 text-xs font-semibold cursor-pointer ${
                appGoogleData.connected ? "text-red-500" : "text-blue-600"
              }`}
            >
              {appGoogleData.connected ? (
                <PackageMinus className="w-4 h-4 mr-2" />
              ) : (
                <PackagePlus className="w-4 h-4 mr-2" />
              )}
              {appGoogleData.connected ? t("Disconnect") : t("Connect")}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between p-2 mb-4 border border-gray-200 rounded-lg shadow-xs">
          <div className="flex items-center space-x-4">
            <div className="pl-2">
              <img
                alt="Microsoft"
                src={`${getBaseUrl()}/static/images/microsoft.png`}
                className="size-5"
              />
            </div>
            <div>
              <div className="text-base font-semibold leading-6 text-gray-900">
                {appAzureData.connected ? appAzureData.name : t("Connect your Microsoft account")}
              </div>
              <div className="mt-1 text-sm leading-5 text-gray-500">
                {appAzureData.connected && appAzureData.email
                  ? appAzureData.email
                  : t("Email and Calendar")}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end space-x-2">
            {appAzureData.connected && currentUser.get("currentInboxAppId") !== "azure" ? (
              <button
                onClick={() => handleSetDefault("azure")}
                type="button"
                className="flex items-center rounded-md px-3 py-2 text-xs font-semibold cursor-pointer text-blue-600"
              >
                {t("Set Default")}
              </button>
            ) : null}
            <button
              onClick={() =>
                appAzureData.connected
                  ? handleDisconnectApp(appAzureData)
                  : handleConnectApp(appAzureData)
              }
              type="button"
              className={`flex items-center rounded-md px-3 py-2 text-xs font-semibold cursor-pointer ${
                appAzureData.connected ? "text-red-500" : "text-blue-600"
              }`}
            >
              {appAzureData.connected ? (
                <PackageMinus className="w-4 h-4 mr-2" />
              ) : (
                <PackagePlus className="w-4 h-4 mr-2" />
              )}
              {appAzureData.connected ? t("Disconnect") : t("Connect")}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={<OctagonAlertIcon />}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default EmailSetting;
